import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ExternalLink,
  Trash2,
  Clock,
  TrendingUp,
  MoreVertical,
  Pencil,
  Activity
} from 'lucide-react';
import StatusBadge from '../UI/StatusBadge';

const MonitorCard = ({ monitor, onDelete, onEdit }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatInterval = (ms) => {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h`;
  };

  const formatLastChecked = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const checked = new Date(date);
    const diffMs = now - checked;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete the monitor for "${monitor.name}"?`)) {
      onDelete(monitor._id);
    }
    setShowMenu(false);
  };

  return (
    <div className="group relative">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-brand-500 to-purple-600 rounded-[2.5rem] blur opacity-0 group-hover:opacity-20 transition duration-500"></div>
      <div className="relative card-premium !p-8 h-full flex flex-col transition-all duration-500 hover:-translate-y-1">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="text-xl font-bold text-surface-900 dark:text-white truncate mb-1.5 tracking-tight group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
              {monitor.name}
            </h3>
            <a
              href={monitor.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold text-surface-500 hover:text-brand-500 dark:hover:text-brand-400 flex items-center gap-2 transition-colors overflow-hidden"
            >
              <span className="truncate max-w-[180px]">{monitor.url.replace(/^https?:\/\//, '')}</span>
              <ExternalLink className="h-3 w-3 flex-shrink-0" />
            </a>
          </div>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2.5 text-surface-400 hover:text-surface-900 dark:hover:text-white rounded-2xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-all border border-transparent hover:border-surface-200 dark:hover:border-surface-700"
            >
              <MoreVertical className="h-5 w-5" />
            </button>

            {showMenu && (
              <div className="absolute right-0 mt-3 w-48 glass-card border border-surface-200/50 dark:border-surface-700/50 rounded-2xl shadow-premium-xl z-50 overflow-hidden animate-scale-in">
                <Link
                  to={`/monitor/${monitor._id}`}
                  className="flex items-center space-x-3 px-5 py-3.5 text-sm font-bold text-surface-700 dark:text-surface-200 hover:bg-brand-500/10 hover:text-brand-600 transition-colors border-b border-surface-100 dark:border-surface-800/50"
                  onClick={() => setShowMenu(false)}
                >
                  <TrendingUp className="h-4 w-4" />
                  <span>Performance</span>
                </Link>
                <button
                  onClick={() => {
                    onEdit(monitor);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-5 py-3.5 text-sm font-bold text-surface-700 dark:text-surface-200 hover:bg-brand-500/10 hover:text-brand-600 transition-colors border-b border-surface-100 dark:border-surface-800/50"
                >
                  <Pencil className="h-4 w-4" />
                  <span>Configure</span>
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full flex items-center space-x-3 px-5 py-3.5 text-sm font-bold text-danger hover:bg-danger/10 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Decommission</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Status indicator and Uptime display */}
        <div className="flex items-center justify-between mb-8 bg-surface-50/50 dark:bg-surface-800/30 p-4 rounded-3xl border border-surface-100 dark:border-surface-700/50">
          <StatusBadge status={monitor.status} />
          <div className="text-right">
            <div className="text-2xl font-black text-surface-900 dark:text-white tracking-tighter">
              {monitor.uptime.toFixed(1)}%
            </div>
            <div className="text-[10px] font-bold text-surface-400 uppercase tracking-widest -mt-1">Operational</div>
          </div>
        </div>

        {/* Technical Data Grid */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="space-y-1">
            <div className="flex items-center space-x-1.5 text-surface-400 mb-1">
              <Clock className="h-3 w-3" />
              <span className="text-[10px] font-bold uppercase tracking-widest">Pulse</span>
            </div>
            <p className="text-sm font-bold text-surface-900 dark:text-white">Every {formatInterval(monitor.interval)}</p>
          </div>
          <div className="space-y-1">
            <div className="flex items-center space-x-1.5 text-surface-400 mb-1">
              <Activity className="h-3 w-3" />
              <span className="text-[10px] font-bold uppercase tracking-widest">Sync</span>
            </div>
            <p className="text-sm font-bold text-surface-900 dark:text-white">{formatLastChecked(monitor.lastChecked)}</p>
          </div>
        </div>

        {/* Health Progress Visualization */}
        <div className="mt-auto space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black text-surface-400 uppercase tracking-widest">Global Stability</span>
            <span className="text-[10px] font-black text-brand-600 dark:text-brand-400 uppercase tracking-widest">{monitor.successfulChecks}/{monitor.totalChecks} Checks</span>
          </div>
          <div className="relative h-2 bg-surface-100 dark:bg-surface-800 rounded-full overflow-hidden border border-surface-100 dark:border-surface-700/50 shadow-inner">
            <div
              className={`absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ${monitor.status === 'up' ? 'bg-gradient-to-r from-brand-600 to-brand-400 shadow-glow-brand' : 'bg-danger shadow-glow-danger'
                }`}
              style={{ width: `${monitor.uptime}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitorCard;
